import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, X, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { DATASETS, TIERS, CURRENCY } from "@/lib/datasets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `${CURRENCY}${n.toFixed(2)}`;
const annual = (n) => `${CURRENCY}${(n * 12).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PricingSection() {
  const [term, setTerm] = useState("1yr");
  const [activeTier, setActiveTier] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", custom_requirements: "" });
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(false);

  const openCheckout = (tier) => {
    setActiveTier(tier);
    setPicked([]);
    setForm({ name: "", email: "", company: "", custom_requirements: "" });
  };
  const closeCheckout = () => !loading && setActiveTier(null);

  const toggleDataset = (id) => {
    const limit = activeTier.datasetCount;
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (limit && prev.length >= limit) {
        toast.error(`This plan allows exactly ${limit} dataset${limit > 1 ? "s" : ""}.`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const t = activeTier;
    if (t.datasetCount && picked.length !== t.datasetCount) {
      toast.error(`Please select exactly ${t.datasetCount} dataset${t.datasetCount > 1 ? "s" : ""}.`);
      return;
    }
    if (t.id === "custom" && !form.custom_requirements.trim()) {
      toast.error("Please describe the custom capabilities you need.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: t.lookup[term],
        origin_url: window.location.origin,
        name: form.name,
        email: form.email,
        company: form.company,
        selected_datasets: t.datasetCount ? picked : DATASETS.map((d) => d.id),
        custom_requirements: form.custom_requirements,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section id="pricing" className="px-6 md:px-12 lg:px-24 py-24 border-t border-white/10 relative z-10">
      <div className="max-w-3xl mb-14">
        <div className="text-xs uppercase tracking-[0.25em] font-mono text-[#FF3B30] mb-3">// Licensing</div>
        <h2 className="text-4xl sm:text-5xl font-black tracking-tighter leading-none mb-5">
          SUBSCRIBE TO GROUND TRUTH.
        </h2>
        <p className="text-[#8E8E93] text-base leading-relaxed">
          Subscriptions are billed annually on a 1-year or 2-year commitment. Every plan includes the monthly
          update cycle for as long as you subscribe — new validated interpretations and skill instructions are
          released when they become available. Choose your datasets and pay securely below.
        </p>
      </div>

      {/* Commitment toggle */}
      <div className="flex items-center gap-4 mb-12 font-mono">
        <div className="inline-flex border border-white/15 bg-black/40 p-1" data-testid="commitment-toggle">
          <button
            onClick={() => setTerm("1yr")}
            className={`px-5 py-2.5 text-xs uppercase tracking-wider transition-colors ${
              term === "1yr" ? "bg-white text-black" : "text-[#8E8E93] hover:text-white"
            }`}
            data-testid="commitment-toggle-1yr"
          >
            1-Year
          </button>
          <button
            onClick={() => setTerm("2yr")}
            className={`px-5 py-2.5 text-xs uppercase tracking-wider transition-colors ${
              term === "2yr" ? "bg-white text-black" : "text-[#8E8E93] hover:text-white"
            }`}
            data-testid="commitment-toggle-2yr"
          >
            2-Year
          </button>
        </div>
        {term === "2yr" && (
          <span className="text-xs text-[#30D158] uppercase tracking-wider flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Locked-in savings applied
          </span>
        )}
      </div>

      {/* Tier grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {TIERS.map((tier, i) => {
          const price = tier.monthly[term];
          const base = tier.monthly["1yr"];
          const discounted = term === "2yr" && price < base;
          return (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
              className={`relative flex flex-col p-6 border bg-[#111315] ${
                tier.featured ? "border-[#FF3B30]/60" : "border-white/10"
              }`}
              data-testid={`pricing-card-${tier.id}`}
            >
              {tier.featured && (
                <span className="absolute -top-px left-6 -translate-y-1/2 bg-[#FF3B30] text-white text-[10px] font-mono uppercase tracking-widest px-2 py-0.5">
                  Most Popular
                </span>
              )}
              <div className="text-xs font-mono uppercase tracking-widest text-[#FF3B30] mb-2">{tier.name}</div>
              <div className="flex items-end gap-1 mb-1">
                <span className="text-3xl font-black tracking-tighter text-white">{fmt(price)}</span>
                <span className="text-[#8E8E93] text-xs font-mono mb-1">/mo</span>
              </div>
              {discounted ? (
                <div className="text-[11px] font-mono text-[#8E8E93] mb-4">
                  <span className="line-through text-[#48484A]">{fmt(base)}</span> · {annual(price)} billed annually
                </div>
              ) : (
                <div className="text-[11px] font-mono text-[#8E8E93] mb-4">
                  {annual(price)} billed annually{tier.noDiscount && term === "2yr" ? " · no discount" : ""}
                </div>
              )}
              <p className="text-[#8E8E93] text-xs leading-relaxed mb-5 min-h-[48px]">{tier.blurb}</p>
              <ul className="space-y-2.5 mb-6 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-[#C7C7CC] leading-snug">
                    <Check className="w-3.5 h-3.5 text-[#30D158] flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => openCheckout(tier)}
                className={`w-full py-3 text-xs font-mono font-bold uppercase tracking-wider transition-colors flex items-center justify-center gap-2 active:scale-[0.98] ${
                  tier.featured
                    ? "bg-[#FF3B30] text-white hover:bg-[#ff5449]"
                    : "bg-white text-black hover:bg-[#FF3B30] hover:text-white"
                }`}
                data-testid={`subscribe-btn-${tier.id}`}
              >
                Subscribe <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          );
        })}
      </div>

      {/* Checkout modal */}
      {activeTier && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={closeCheckout}
          data-testid="checkout-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#0c0d0e] border border-white/15 p-7"
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-[#FF3B30] mb-1">
                  {activeTier.name} · {term === "2yr" ? "2-Year" : "1-Year"}
                </div>
                <div className="text-2xl font-black tracking-tighter">
                  {fmt(activeTier.monthly[term])}
                  <span className="text-xs font-mono text-[#8E8E93] ml-1">/mo · {annual(activeTier.monthly[term])} billed annually</span>
                </div>
              </div>
              <button onClick={closeCheckout} className="text-[#8E8E93] hover:text-white" data-testid="checkout-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubscribe} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  required placeholder="Full name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none transition-colors"
                  data-testid="checkout-name-input"
                />
                <input
                  required placeholder="Company" value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none transition-colors"
                  data-testid="checkout-company-input"
                />
              </div>
              <input
                required type="email" placeholder="Work email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none transition-colors"
                data-testid="checkout-email-input"
              />

              {activeTier.datasetCount && (
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider text-[#8E8E93] mb-3">
                    Select {activeTier.datasetCount} dataset{activeTier.datasetCount > 1 ? "s" : ""}
                    <span className="text-[#FF3B30] ml-2">{picked.length}/{activeTier.datasetCount}</span>
                  </div>
                  <div className="space-y-2">
                    {DATASETS.map((d) => {
                      const sel = picked.includes(d.id);
                      return (
                        <button
                          type="button" key={d.id} onClick={() => toggleDataset(d.id)}
                          className={`w-full flex items-center gap-3 p-3 border text-left transition-colors ${
                            sel ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/10 hover:border-white/30"
                          }`}
                          data-testid={`checkout-dataset-${d.id}`}
                        >
                          <span className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${sel ? "bg-[#FF3B30] border-[#FF3B30]" : "border-white/30"}`}>
                            {sel && <Check className="w-3 h-3 text-white" />}
                          </span>
                          <span className="text-xs font-mono text-[#48484A] w-6">{d.index}</span>
                          <span className="text-sm text-white">{d.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {!activeTier.datasetCount && activeTier.id !== "custom" && (
                <div className="text-xs font-mono text-[#8E8E93] border border-white/10 bg-black/40 p-3">
                  Includes all {DATASETS.length} datasets + every monthly refresh.
                </div>
              )}

              {activeTier.id === "custom" && (
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider text-[#8E8E93] mb-2">
                    Describe the custom agent skill you need (~1 week turnaround / month)
                  </div>
                  <textarea
                    required rows="3" value={form.custom_requirements}
                    onChange={(e) => setForm({ ...form, custom_requirements: e.target.value })}
                    placeholder="e.g. a deposit-dispute resolution skill tuned for my booking flow…"
                    className="w-full bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none transition-colors resize-none"
                    data-testid="checkout-custom-textarea"
                  />
                </div>
              )}

              <button
                type="submit" disabled={loading}
                className="w-full bg-[#FF3B30] text-white hover:bg-[#ff5449] font-mono font-bold py-3.5 uppercase tracking-wider text-xs transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="checkout-submit-btn"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Continue to secure payment <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-[10px] font-mono text-[#48484A] text-center">
                Billed annually in AUD. Tax calculated by Stripe at checkout. Test card 4242 4242 4242 4242.
              </p>
            </form>
          </motion.div>
        </div>
      )}
    </section>
  );
}
